import api from './api';

/**
 * Build a real Error that still carries the fields callers read.
 *
 * These paths used to `throw { message, status, data }`. A plain object is not
 * an Error: it has no stack, fails `instanceof Error`, and anything that
 * rethrows or logs it loses the context. Attaching the same properties to an
 * Error keeps every existing `err.message` / `err.status` reader working.
 */
const authError = (error, fallbackMessage, fallbackStatus = 500) => {
  const err = new Error(error?.response?.data?.message || fallbackMessage);
  err.status = error?.response?.status || fallbackStatus;
  err.data = error?.response?.data || null;
  return err;
};

export const authService = {
  // Register new user
  register: async (userData) => {
    try {
      const response = await api.post('/auth/signup', userData);
      // Check success inside response.data
      if (response.data && response.data.success && response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      // Return response.data so the component gets { success: true, user: ... }
      return response.data; 
    } catch (error) {
      console.error('Register error:', error);
      // Return a consistent error structure
      throw authError(error, 'Registration failed');
    }
  },

  // Login user
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      const { data } = response;

      // Check different response structures and save to localStorage
      if (data) {
        let userData = null;
        let token = null;
        
        // Handle different response structures
        if (data.user && data.token) {
          // Structure: { user: {...}, token: '...' }
          userData = data.user;
          token = data.token;
        } else if (data.data?.user && data.data?.token) {
          // Structure: { data: { user: {...}, token: '...' } }
          userData = data.data.user;
          token = data.data.token;
        } else if (data.success && data.user && data.token) {
          // Structure: { success: true, user: {...}, token: '...' }
          userData = data.user;
          token = data.token;
        } else if (data.id || data._id) {
          // Direct user object with token property
          userData = data;
          token = data.token;
        }
        
        if (userData && token) {
          // Ensure user data has proper structure
          const finalUserData = {
            id: userData.id || userData._id,
            email: userData.email,
            name: userData.name || userData.username || userData.email?.split('@')[0] || 'User',
            role: (userData.role || 'student').toLowerCase()
          };
          
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(finalUserData));
        }
      }

      return data; 
    } catch (error) {
      console.error('Login error:', error);
      throw authError(error, 'Login failed');
    }
  },

  // Get current user profile
  getProfile: async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.warn('No token found when trying to get profile');
        throw authError(null, 'No authentication token found', 401);
      }
      
      const response = await api.get('/auth/me');
      return response.data; 
    } catch (error) {
      console.error('Get profile error:', error);
      
      // Don't clear token on server errors (500) - might be temporary server issue
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('Invalid token (401/403), clearing storage');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } else if (error.response?.status === 500) {
        console.warn('Server error (500) when fetching profile. Token may still be valid.');
      }
      
      throw authError(error, 'Failed to fetch profile');
    }
  },

  // Update basic profile fields (name, phone, email)
  updateProfile: async (payload) => {
    try {
      const response = await api.put('/auth/update-profile', payload);
      // If backend returns updated user, keep localStorage in sync
      if (response.data?.success && response.data?.data) {
        const u = response.data.data;
        const finalUserData = {
          id: u.id,
          email: u.email,
          name: u.name || u.username || u.email?.split('@')[0] || 'User',
          role: (u.role || 'student').toLowerCase()
        };
        localStorage.setItem('user', JSON.stringify(finalUserData));
      }
      return response.data;
    } catch (error) {
      throw authError(error, 'Failed to update profile');
    }
  },

  // Logout user
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const token = localStorage.getItem('token');
    const hasToken = !!token;
    return hasToken;
  },

  // Get current user data from localStorage
  getCurrentUser: () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        return null;
      }
      
      const user = JSON.parse(userStr);
      return user;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      localStorage.removeItem('user');
      return null;
    }
  }
};